import React, {
    createContext, useContext, useState,
    useEffect, useCallback, useMemo,
} from 'react';
import { convertCents, formatCurrency, getExchangeRate } from '../utils/currency';

const CurrencyContext = createContext(null);

const RATES_CACHE_KEY  = 'haytour_exchange_rates';
const RATES_TTL_MS     = 60 * 60 * 1000; // 1 hour
const DEFAULT_CURRENCY = 'EUR';
const API_URL          = 'https://open.er-api.com/v6/latest/EUR';

// Fallback rates relative to EUR — used when the API is unreachable.
const FALLBACK_RATES = {
    EUR: 1, USD: 1.08, AMD: 420, GBP: 0.86, RUB: 100,
    JPY: 161, CNY: 7.8, AUD: 1.65, CAD: 1.46, CHF: 0.97,
};

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(
        () => localStorage.getItem('haytour_currency') || DEFAULT_CURRENCY
    );
    const [rates,        setRates]        = useState(FALLBACK_RATES);
    const [ratesLoading, setRatesLoading] = useState(false);

    const fetchRates = useCallback(async () => {
        // Try cache first
        try {
            const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || 'null');
            if (cached && Date.now() - cached.timestamp < RATES_TTL_MS) {
                setRates(cached.rates);
                return;
            }
        } catch { /* corrupt cache — refetch */ }

        setRatesLoading(true);
        try {
            const res  = await fetch(API_URL);
            const data = await res.json();
            if (data?.rates) {
                const r = { ...data.rates, EUR: 1 };
                setRates(r);
                localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
                    rates: r, timestamp: Date.now(),
                }));
            }
        } catch { /* keep fallback rates */ }
        finally { setRatesLoading(false); }
    }, []);

    useEffect(() => { fetchRates(); }, [fetchRates]);

    const setCurrency = useCallback((code) => {
        setCurrencyState(code);
        localStorage.setItem('haytour_currency', code);
    }, []);

    /** Convert minor units from fromCurrency to the user's selected currency. */
    const convert = useCallback(
        (cents, fromCurrency) => convertCents(cents, fromCurrency || DEFAULT_CURRENCY, currency, rates),
        [currency, rates]
    );

    /** Convert + format as a localized string in the user's selected currency. */
    const format = useCallback(
        (cents, fromCurrency) => formatCurrency(convert(cents, fromCurrency), currency),
        [convert, currency]
    );

    /** Exchange rate from fromCurrency to the user's selected currency. */
    const exchangeRate = useCallback(
        (fromCurrency) => getExchangeRate(fromCurrency || DEFAULT_CURRENCY, currency, rates),
        [currency, rates]
    );

    const value = useMemo(() => ({
        currency, setCurrency, rates, ratesLoading,
        convert, format, exchangeRate,
    }), [currency, setCurrency, rates, ratesLoading, convert, format, exchangeRate]);

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error('useCurrency must be used inside <CurrencyProvider>');
    return ctx;
}
